import React, { useState, useEffect, useCallback } from 'react';
import {
  Camera, Plus, Download, Trash2, Eye, Loader2, MapPin, Calendar,
  Star, Upload, X, ImageIcon, Check, XCircle, ArrowLeft, GripVertical,
  Archive, CheckCircle, Filter
} from 'lucide-react';
import axios from 'axios';
import logger from '../../../utils/logger';
import PublishToGalleryModal from '../../../components/admin/PublishToGalleryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-600',
  active: 'bg-green-50 text-green-700',
  closed: 'bg-amber-50 text-amber-700',
  archived: 'bg-slate-100 text-slate-500',
};

const PHOTO_STATUS_STYLES = {
  approved: 'bg-green-50 text-green-700 border-green-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
};

/* ========== Gallery Form ========== */
const GalleryForm = ({ form, setForm, onSave, onCancel, saving, isEditing, onCoverUpload, uploadingCover }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6" data-testid="gallery-form">
    <h3 className="font-heading font-bold text-sm text-[#0A0A0A] mb-4">
      {isEditing ? 'Edit Gallery' : 'Create New Event Gallery'}
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <label className="block text-xs font-semibold text-[#4B5563] mb-1">Event Title *</label>
        <input type="text" value={form.event_title}
          onChange={(e) => setForm(p => ({ ...p, event_title: e.target.value }))}
          className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
          placeholder="e.g., Annual Gala 2026" data-testid="gallery-form-title" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#4B5563] mb-1">Event Date</label>
        <input type="date" value={form.event_date}
          onChange={(e) => setForm(p => ({ ...p, event_date: e.target.value }))}
          className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
          data-testid="gallery-form-date" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#4B5563] mb-1">Location</label>
        <input type="text" value={form.event_location}
          onChange={(e) => setForm(p => ({ ...p, event_location: e.target.value }))}
          className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
          placeholder="e.g., Brooklyn Community Center, NY" data-testid="gallery-form-location" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#4B5563] mb-1">Upload Window</label>
        <select value={form.upload_window_hours}
          onChange={(e) => setForm(p => ({ ...p, upload_window_hours: parseInt(e.target.value) }))}
          className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
          data-testid="gallery-form-window">
          <option value={24}>24 hours</option>
          <option value={48}>48 hours</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#4B5563] mb-1">Gallery Type</label>
        <select value={form.gallery_type || 'event'}
          onChange={(e) => setForm(p => ({ ...p, gallery_type: e.target.value }))}
          className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
          data-testid="gallery-form-type">
          <option value="event">Event Gallery</option>
          <option value="impact">Impact Gallery</option>
        </select>
      </div>
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-[#4B5563] mb-1">Moderation Mode</label>
      <select value={form.moderation_mode}
        onChange={(e) => setForm(p => ({ ...p, moderation_mode: e.target.value }))}
        className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
        data-testid="gallery-form-moderation">
        <option value="auto_first_20">Auto-approve first 20, then require review</option>
        <option value="auto_all">Auto-approve all uploads</option>
        <option value="manual">Require review for all uploads</option>
      </select>
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-[#4B5563] mb-1">Description</label>
      <textarea value={form.event_description}
        onChange={(e) => setForm(p => ({ ...p, event_description: e.target.value }))}
        className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
        rows={2} placeholder="Brief description of the event" data-testid="gallery-form-description" />
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-[#4B5563] mb-1">Cover Image</label>
      <div className="flex items-center gap-3">
        {form.cover_image ? (
          <div className="relative w-20 h-14 rounded overflow-hidden border border-[#E5E7EB]">
            <img src={form.cover_image} alt="Cover" className="w-full h-full object-cover" />
            <button onClick={() => setForm(p => ({ ...p, cover_image: '' }))}
              className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/60 rounded-full flex items-center justify-center">
              <X className="w-2.5 h-2.5 text-white" />
            </button>
          </div>
        ) : (
          <div className="w-20 h-14 rounded border border-dashed border-[#D1D5DB] flex items-center justify-center bg-[#F8F9FA]">
            <ImageIcon className="w-5 h-5 text-[#D1D5DB]" />
          </div>
        )}
        <label className="cursor-pointer text-xs font-semibold text-[#BA1826] hover:underline flex items-center gap-1">
          {uploadingCover ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {form.cover_image ? 'Change' : 'Upload'}
          <input type="file" accept="image/*" className="hidden" onChange={onCoverUpload} disabled={uploadingCover} />
        </label>
      </div>
    </div>
    <div className="flex items-center gap-3">
      <button onClick={onSave} disabled={saving || !form.event_title.trim()}
        className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50" data-testid="gallery-form-save">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        {isEditing ? 'Save Changes' : 'Create Gallery'}
      </button>
      {onCancel && (
        <button onClick={onCancel} className="text-xs text-[#4B5563] hover:text-[#0A0A0A]" data-testid="gallery-form-cancel">Cancel</button>
      )}
    </div>
  </div>
);

/* ========== Photo Management View ========== */
const PhotoManagement = ({ gallery, onBack, onRefreshGalleries }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [bulkApproving, setBulkApproving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/galleries/${gallery.id}/photos?status=${filter}`,
        { withCredentials: true }
      );
      setPhotos(res.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [gallery.id, filter]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const updatePhoto = async (photoId, data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/galleries/${gallery.id}/photos/${photoId}`, data, { withCredentials: true });
      fetchPhotos();
      onRefreshGalleries();
    } catch (e) { alert('Update failed'); }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo permanently?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/galleries/${gallery.id}/photos/${photoId}`, { withCredentials: true });
      fetchPhotos();
      onRefreshGalleries();
    } catch (e) { alert('Delete failed'); }
  };

  const bulkApprove = async () => {
    setBulkApproving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/galleries/${gallery.id}/photos/bulk-approve`, {}, { withCredentials: true });
      alert(`${res.data.approved} photos approved.`);
      fetchPhotos();
      onRefreshGalleries();
    } catch (e) { alert('Bulk approve failed'); }
    setBulkApproving(false);
  };

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/galleries/${gallery.id}/photos/download`,
        { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `NHF_${gallery.event_title.replace(/\s/g, '_')}_Photos.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.status === 404 ? 'No approved photos to download' : 'Download failed');
    }
    setDownloading(false);
  };

  const movePhoto = async (photoId, direction) => {
    const idx = photos.findIndex(p => p.id === photoId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= photos.length) return;

    const newOrder = photos.map(p => p.id);
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    try {
      await axios.put(`${BACKEND_URL}/api/admin/galleries/${gallery.id}/photos/reorder`,
        { order: newOrder }, { withCredentials: true });
      fetchPhotos();
    } catch (e) { logger.error(e); }
  };

  const pendingCount = photos.filter(p => p.status === 'pending').length;
  const approvedCount = photos.filter(p => p.status === 'approved').length;
  const featuredCount = photos.filter(p => p.is_featured).length;

  return (
    <div className="space-y-5" data-testid="photo-management">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="text-sm text-[#4B5563] hover:text-[#0A0A0A] flex items-center gap-1" data-testid="photos-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">{gallery.event_title}</h2>
          <p className="text-xs text-[#4B5563]">
            {approvedCount} approved &middot; {pendingCount} pending &middot; {featuredCount} featured
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <button onClick={bulkApprove} disabled={bulkApproving}
              className="text-xs font-semibold px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 flex items-center gap-1 disabled:opacity-50"
              data-testid="bulk-approve-btn">
              {bulkApproving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Approve All ({pendingCount})
            </button>
          )}
          {approvedCount > 0 && (
            <button onClick={() => setPublishOpen(true)}
              className="text-xs font-semibold px-3 py-1.5 rounded border border-[#BA1826] text-[#BA1826] hover:bg-[#FEF2F2] flex items-center gap-1"
              data-testid="publish-to-gallery-btn"
              title="Publish approved photos from this event to the public Main Gallery">
              <ImageIcon className="w-3 h-3" /> Publish to Main Gallery
            </button>
          )}
          {approvedCount > 0 && (
            <button onClick={downloadZip} disabled={downloading}
              className="text-xs font-semibold px-3 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1 disabled:opacity-50"
              data-testid="download-zip-btn">
              {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
              Download ZIP
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2" data-testid="photo-filters">
        <Filter className="w-3.5 h-3.5 text-[#9CA3AF]" />
        {['all', 'pending', 'approved', 'rejected'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-[10px] font-semibold px-2.5 py-1 rounded-full capitalize transition-colors ${
              filter === f ? 'bg-[#0A0A0A] text-white' : 'bg-[#F1F5F9] text-[#4B5563] hover:bg-[#E5E7EB]'
            }`} data-testid={`filter-${f}`}>
            {f}
          </button>
        ))}
      </div>

      {/* Photo Grid */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : photos.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
          <Camera className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
          <p className="text-sm text-[#4B5563]">{filter === 'all' ? 'No photos uploaded yet' : `No ${filter} photos`}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {photos.map((p, idx) => (
            <div key={p.id} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden group" data-testid={`admin-photo-${p.id}`}>
              <div className="aspect-square relative">
                <img src={p.image_url} alt={p.guest_name || 'Photo'} className="w-full h-full object-cover" loading="lazy" />
                {/* Status Badge */}
                <span className={`absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded border ${PHOTO_STATUS_STYLES[p.status]}`}>
                  {p.status}
                </span>
                {/* Featured Badge */}
                {p.is_featured && (
                  <span className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#BA1826] rounded-full flex items-center justify-center">
                    <Star className="w-3 h-3 text-white" fill="white" />
                  </span>
                )}
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
                  {p.status !== 'approved' && (
                    <button onClick={() => updatePhoto(p.id, { status: 'approved' })}
                      className="w-7 h-7 bg-green-600 rounded-full flex items-center justify-center text-white hover:bg-green-700" title="Approve">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {p.status !== 'rejected' && (
                    <button onClick={() => updatePhoto(p.id, { status: 'rejected' })}
                      className="w-7 h-7 bg-amber-500 rounded-full flex items-center justify-center text-white hover:bg-amber-600" title="Reject">
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => updatePhoto(p.id, { is_featured: !p.is_featured })}
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-white ${p.is_featured ? 'bg-[#BA1826]' : 'bg-gray-500 hover:bg-[#BA1826]'}`} title="Toggle Featured">
                    <Star className="w-3.5 h-3.5" fill={p.is_featured ? 'white' : 'none'} />
                  </button>
                  <button onClick={() => deletePhoto(p.id)}
                    className="w-7 h-7 bg-red-600 rounded-full flex items-center justify-center text-white hover:bg-red-700" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="p-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#4B5563] truncate flex-1">{p.guest_name || 'Anonymous'}</p>
                  <div className="flex items-center gap-0.5">
                    <button onClick={() => movePhoto(p.id, 'up')} disabled={idx === 0}
                      className="text-[#9CA3AF] hover:text-[#0A0A0A] disabled:opacity-20 p-0.5" title="Move up">
                      <GripVertical className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <PublishToGalleryModal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        sourceType="event_qr"
        sourceId={gallery.id}
        defaultAlbumSlug="events"
        photos={photos
          .filter((p) => p.status === 'approved')
          .map((p) => ({
            id: p.id,
            image_url: p.image_url,
            thumb_url: p.image_url,
            title: gallery.event_title || null,
            caption: p.guest_name || null,
          }))}
      />
    </div>
  );
};

/* ========== Main Admin Gallery Tab ========== */
const AdminGalleryTab = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [managingGallery, setManagingGallery] = useState(null);
  const emptyForm = {
    event_title: '', event_date: '', event_location: '', event_description: '',
    cover_image: '', upload_window_hours: 24, moderation_mode: 'auto_first_20',
    gallery_type: 'event'
  };
  const [form, setForm] = useState(emptyForm);

  const fetchGalleries = useCallback(async () => {
    try {
      const galRes = await axios.get(`${BACKEND_URL}/api/admin/galleries`, { withCredentials: true });
      setGalleries(galRes.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGalleries(); }, [fetchGalleries]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/galleries/${editingId}`, form, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/galleries`, form, { withCredentials: true });
      }
      setForm(emptyForm);
      setShowForm(false);
      setEditingId(null);
      fetchGalleries();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save gallery'); }
    setSaving(false);
  };

  const handleEdit = (g) => {
    setForm({
      event_title: g.event_title || '', event_date: g.event_date || '',
      event_location: g.event_location || '', event_description: g.event_description || '',
      cover_image: g.cover_image || '', upload_window_hours: g.upload_window_hours || 24,
      moderation_mode: g.moderation_mode || 'auto_first_20',
      gallery_type: g.gallery_type || 'event'
    });
    setEditingId(g.id);
    setShowForm(true);
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete gallery "${g.event_title}"? This removes all photos too.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/galleries/${g.id}`, { withCredentials: true });
      fetchGalleries();
    } catch (e) { alert('Delete failed'); }
  };

  const handleStatusChange = async (g, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/galleries/${g.id}`, { status: newStatus }, { withCredentials: true });
      fetchGalleries();
    } catch (e) { alert('Status update failed'); }
  };

  const handleToggleFeatured = async (g) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/galleries/${g.id}`, { is_featured: !g.is_featured }, { withCredentials: true });
      fetchGalleries();
    } catch (e) { alert('Failed to update featured status'); }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(p => ({ ...p, cover_image: res.data.url }));
    } catch { alert('Upload failed'); }
    setUploadingCover(false);
    e.target.value = '';
  };

  // If managing a gallery's photos, show PhotoManagement view
  if (managingGallery) {
    return (
      <PhotoManagement
        gallery={managingGallery}
        onBack={() => { setManagingGallery(null); fetchGalleries(); }}
        onRefreshGalleries={fetchGalleries}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="gallery-admin-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Event Photo Galleries</h2>
          <p className="text-xs text-[#4B5563]">Create event galleries with QR codes for guest photo uploads</p>
        </div>
        {!showForm && (
          <button onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
            className="btn-primary text-xs flex items-center gap-1.5" data-testid="gallery-create-btn">
            <Plus className="w-3.5 h-3.5" /> New Gallery
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <GalleryForm form={form} setForm={setForm} onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingId(null); setForm(emptyForm); }}
          saving={saving} isEditing={!!editingId} onCoverUpload={handleCoverUpload} uploadingCover={uploadingCover} />
      )}

      {/* Gallery List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : galleries.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
          <Camera className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
          <p className="text-sm text-[#4B5563]">No event galleries yet</p>
          <p className="text-xs text-[#9CA3AF] mt-1">Create your first gallery and generate a QR code for your next event</p>
        </div>
      ) : (
        <div className="space-y-3">
          {galleries.map((g) => (
            <div key={g.id} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`gallery-card-${g.id}`}>
              <div className="flex flex-col sm:flex-row">
                {/* Cover */}
                <div className="sm:w-36 h-24 sm:h-auto bg-[#F8F9FA] flex-shrink-0 cursor-pointer" onClick={() => setManagingGallery(g)}>
                  {g.cover_image ? (
                    <img src={g.cover_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center min-h-[96px]">
                      <Camera className="w-6 h-6 text-[#D1D5DB]" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 p-4">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <h3 className="font-heading font-bold text-sm text-[#0A0A0A] cursor-pointer hover:text-[#BA1826]"
                      onClick={() => setManagingGallery(g)}>{g.event_title}</h3>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${STATUS_STYLES[g.status]}`}>
                      {g.status}
                    </span>
                    {g.is_featured && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#BA1826] uppercase">Featured</span>
                    )}
                    {g.gallery_type === 'impact' && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 uppercase">Impact</span>
                    )}
                    {g.photo_count > 0 && g.photo_count > g.approved_count && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                        {g.photo_count - g.approved_count} pending
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-xs text-[#4B5563] mb-3">
                    {g.event_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{g.event_date}</span>}
                    {g.event_location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{g.event_location}</span>}
                    <span className="flex items-center gap-1"><Camera className="w-3 h-3" />{g.approved_count || 0} approved / {g.photo_count || 0} total</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {g.status === 'draft' && (
                      <button onClick={() => handleStatusChange(g, 'active')}
                        className="text-[10px] font-semibold px-2.5 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                        data-testid={`gallery-activate-${g.id}`}>Activate</button>
                    )}
                    {g.status === 'active' && (
                      <button onClick={() => handleStatusChange(g, 'closed')}
                        className="text-[10px] font-semibold px-2.5 py-1 rounded bg-amber-500 text-white hover:bg-amber-600"
                        data-testid={`gallery-close-${g.id}`}>Close Uploads</button>
                    )}
                    {g.status === 'closed' && (
                      <button onClick={() => handleStatusChange(g, 'archived')}
                        className="text-[10px] font-semibold px-2.5 py-1 rounded bg-slate-500 text-white hover:bg-slate-600"
                        data-testid={`gallery-archive-${g.id}`}>Archive</button>
                    )}
                    {(g.status === 'closed' || g.status === 'archived') && (
                      <button onClick={() => handleStatusChange(g, 'active')}
                        className="text-[10px] font-semibold px-2.5 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200"
                        data-testid={`gallery-reopen-${g.id}`}>Reopen</button>
                    )}

                    <div className="h-4 w-px bg-[#E5E7EB]" />

                    <button onClick={() => setManagingGallery(g)}
                      className="text-[10px] font-semibold px-2.5 py-1 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1"
                      data-testid={`gallery-photos-${g.id}`}>
                      <Camera className="w-3 h-3" /> Photos
                    </button>

                    <button onClick={() => handleToggleFeatured(g)}
                      className={`text-[10px] font-semibold px-2.5 py-1 rounded border flex items-center gap-1 ${
                        g.is_featured ? 'border-[#BA1826] text-[#BA1826] bg-[#FEE2E2]' : 'border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA]'
                      }`} data-testid={`gallery-featured-${g.id}`}>
                      <Star className="w-3 h-3" fill={g.is_featured ? '#BA1826' : 'none'} /> Featured
                    </button>

                    <a href={`/gallery/${g.id}/upload`} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] font-semibold px-2.5 py-1 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1"
                      data-testid={`gallery-preview-${g.id}`}>
                      <Eye className="w-3 h-3" /> Preview
                    </a>

                    <button onClick={() => handleEdit(g)}
                      className="text-[10px] font-semibold px-2.5 py-1 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA]"
                      data-testid={`gallery-edit-${g.id}`}>Edit</button>

                    <button onClick={() => handleDelete(g)}
                      className="text-[10px] font-semibold px-2.5 py-1 rounded border border-red-200 text-red-500 hover:bg-red-50 flex items-center gap-1"
                      data-testid={`gallery-delete-${g.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGalleryTab;
